import React, { useState, useEffect } from 'react';
import '../../styles/modal.css';
import '../../styles/test-cases-overview.css';
import '../../styles/version-history.css';
import axiosInstance from '../../api/axiosInstance';
import { stepItemToText } from './testCaseStepUtils';
import { formatDate, formatDateTime } from '../../utils/dateTimeFormat';

const MAX_NAME_CHARS = 16;

const truncateByChars = (text = '', maxChars = MAX_NAME_CHARS) => {
  const trimmed = (text || '').trim();
  if (!trimmed) return { display: '', full: '', isTruncated: false };

  if (trimmed.length <= maxChars) {
    return { display: trimmed, full: trimmed, isTruncated: false };
  }

  return {
    display: `${trimmed.slice(0, maxChars)}...`,
    full: trimmed,
    isTruncated: true,
  };
};

const TruncatedTestCaseName = ({ name, className = 'metadata-value', title }) => {
  const { display, full, isTruncated } = truncateByChars(name);
  const tooltipTitle = title ?? (isTruncated ? full : undefined);

  return (
    <span className={className} title={tooltipTitle}>
      {display}
    </span>
  );
};

const TestCaseVersionHistory = ({ isOpen, onClose, testCase }) => {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVersions, setSelectedVersions] = useState([]);
  const [compareMode, setCompareMode] = useState(false);
  const [versionDetails, setVersionDetails] = useState({});
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [comparisonLoading, setComparisonLoading] = useState(false);
  const [detailsError, setDetailsError] = useState(null);
  const [comparisonError, setComparisonError] = useState(null);
  const [retryCount, setRetryCount] = useState({});

  useEffect(() => {
    if (isOpen && testCase) {
      setLoading(true);
      setSelectedVersions([]); 
      setCompareMode(false); 
      fetchVersions();
    }
  }, [isOpen, testCase]);

  useEffect(() => {
  if (versions.length > 0 && !compareMode && selectedVersions.length === 0) {
    const firstVersion = versions[0];
    const versionKey = `${firstVersion.test_case_id}_${firstVersion.version}`;
    setSelectedVersions([versionKey]);
    fetchVersionDetails(firstVersion.test_case_id, firstVersion.version);
  }
}, [versions]);

  const fetchVersions = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/test_case/list_test_case_version?test_case_id=${testCase.id}`);
      const normalizedVersions = (response.versions || []).map(v => ({
      ...v,
      test_case_id: response.test_case_id 
    }));
      setVersions(normalizedVersions);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching versions:', error);
      setLoading(false);
    }
  };

  // Fetch details for a single version with retry mechanism
  const fetchVersionDetails = async (testCaseId, version, retries = 0) => {
    const MAX_RETRIES = 3;
    const RETRY_DELAY = 1000; // ms

    try {
      setDetailsLoading(true);
      setDetailsError(null);
      
      const payload = [
        {
          test_case_id: testCaseId,
          version: version
        }
      ];

      const response = await axiosInstance.post('/test_case/get_test_case_version', payload);
      
      if (response?.results && response.results.length > 0) {
        const versionKey = `${testCaseId}_${version}`;
        const flattenedDetails = flattenVersionResponse(response.results[0]);
        setVersionDetails(prev => ({
          ...prev,
          [versionKey]: flattenedDetails
        }));
        setDetailsError(null);
        setRetryCount(prev => ({ ...prev, [versionKey]: 0 }));
      }
      
      setDetailsLoading(false);
    } catch (error) {
      console.error('Error fetching version details:', error);
      
      if (retries < MAX_RETRIES) {
        setTimeout(() => {
          const versionKey = `${testCaseId}_${version}`;
          setRetryCount(prev => ({ ...prev, [versionKey]: (prev[versionKey] || 0) + 1 }));
          fetchVersionDetails(testCaseId, version, retries + 1);
        }, RETRY_DELAY);
      } else {
        setDetailsError('Failed to load version details. Please try again.');
        setDetailsLoading(false);
      }
    }
  };

  // Fetch details for two versions in comparison mode
  const fetchComparisonDetails = async (selectedVersionList) => {
    const MAX_RETRIES = 3;
    const RETRY_DELAY = 1000;

    if (selectedVersionList.length !== 2) return;

    try {
      setComparisonLoading(true);
      setComparisonError(null);

      // Parse version keys to extract test_case_id and version
      const payload = selectedVersionList.map(versionKey => {
        const [test_case_id, version] = versionKey.split('_');
        return {
          test_case_id: parseInt(test_case_id),
          version: version
        };
      });

      const response = await axiosInstance.post('/test_case/get_test_case_version', payload);
      
      if (response?.results && response.results.length === 2) {
        const comparisonKey = selectedVersionList.join('__');
        const flattenedResults = response.results.map(result => 
          flattenVersionResponse(result)
        );
        setVersionDetails(prev => ({
          ...prev,
          [comparisonKey]: flattenedResults
        }));
        setComparisonError(null);
        setRetryCount(prev => ({ ...prev, [comparisonKey]: 0 }));
      }
      
      setComparisonLoading(false);
    } catch (error) {
      console.error('Error fetching comparison details:', error);
      setComparisonError('Failed to load comparison. Please try again.');
      setComparisonLoading(false);
    }
  };

    const flattenVersionResponse = (result) => {
      if (!result?.versions || result.versions.length === 0) return null;
      
      const inner = result.versions[0];
      return {
        test_case_id: result.test_case_id,  
        version: result.version,            
        name: inner.name,
        description: inner.description,
        created_by: inner.created_by,
        created_by_id: inner.created_by_id,
        created_at: inner.created_at,
        step_description: inner.step_description ?? [],
        expected_result: inner.expected_result ?? [],
        is_current: inner.is_current ?? false
      };
    };

  const handleVersionSelect = (version) => {
    const versionKey = `${version.test_case_id}_${version.version}`;
    
    if (compareMode) {
      // In compare mode, toggle selection (max 2)
      setSelectedVersions(prev => {
        if (prev.includes(versionKey)) {
          return prev.filter(id => id !== versionKey);
        } else {
          if (prev.length < 2) {
            const newSelection = [...prev, versionKey];
            // Fetch comparison details when 2 versions are selected
            if (newSelection.length === 2) {
              fetchComparisonDetails(newSelection);
            }
            return newSelection;
          }
          return prev;
        }
      });
    } else {
      // In view mode, select only one and fetch its details
      setSelectedVersions([versionKey]);
      fetchVersionDetails(version.test_case_id, version.version);
    }
  };

  const getVersionById = (versionKey) => {
    if (!versionKey) return null;
    const [test_case_id, version] = versionKey.split('_');
    return versions.find(v => v.test_case_id === parseInt(test_case_id) && v.version === version);
  };

  // Skeleton Loader Component
  const SkeletonLoader = () => (
    <div className="skeleton-loader" style={{ animation: "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite" }}>
      <div style={{
        height: "20px",
        backgroundColor: "#e9ecef",
        borderRadius: "4px",
        marginBottom: "12px",
        width: "60%"
      }}></div>
      <div style={{
        height: "16px",
        backgroundColor: "#e9ecef",
        borderRadius: "4px",
        marginBottom: "8px",
        width: "100%"
      }}></div>
      <div style={{
        height: "16px",
        backgroundColor: "#e9ecef",
        borderRadius: "4px",
        marginBottom: "8px",
        width: "95%"
      }}></div>
      <div style={{
        height: "16px",
        backgroundColor: "#e9ecef",
        borderRadius: "4px",
        width: "90%"
      }}></div>
    </div>
  );

  const renderVersionDetails = (versionKey) => {
    if (!versionKey) return null;

    const details = versionDetails[versionKey];

    if (detailsLoading) {
      return (
        <div className="version-details">
          <SkeletonLoader />
          <SkeletonLoader />
          <SkeletonLoader />
        </div>
      );
    }

    if (detailsError) {
      return (
        <div className="version-details error-state" style={{ padding: "20px", textAlign: "center" }}>
          <p style={{ color: "#dc3545", marginBottom: "10px" }}>{detailsError}</p>
          <button 
            onClick={() => {
              const [test_case_id, version] = versionKey.split('_');
              fetchVersionDetails(parseInt(test_case_id), version);
            }}
            style={{
              padding: "8px 16px",
              backgroundColor: "#007bff",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer"
            }}
          >
            Retry
          </button>
        </div>
      );
    }

    if (!details) return null;

    return (
      <div className="version-details">
        <h3>Version {details.version} Details</h3>
        
        <div className="version-metadata">
          <div className="metadata-item">
            <span className="metadata-label">Name:</span>
            <TruncatedTestCaseName name={details.name} />
          </div>
          <div className="metadata-item">
            <span className="metadata-label">Created:</span>
            <span className="metadata-value">{formatDateTime(details.created_at)}</span>
          </div>
          <div className="metadata-item">
            <span className="metadata-label">Created By:</span>
            <span className="metadata-value">{details.created_by}</span>
          </div>
        </div>
        
        <div className="version-content">
          <div className="version-content-section">
            <h4>Description</h4>
            <p className="version-description">{details.description || 'No description'}</p>
          </div>
          <div className="version-content-section">
            <h4>Steps</h4>
            <ol style={{ marginLeft: "20px", lineHeight: "1.6" }}>
              {details.step_description && Array.isArray(details.step_description) ? (
                details.step_description.map((step, index) => (
                  <li key={index}>
                  {stepItemToText(step)}
                </li>
                ))
              ) : (
                <li>{details.step_description || 'No steps defined'}</li>
              )}
            </ol>
          </div>
          
          <div className="version-content-section">
            <h4>Expected Results</h4>
            <ol style={{ marginLeft: "20px", lineHeight: "1.6" }}>
              {details.expected_result && Array.isArray(details.expected_result) ? (
                details.expected_result.map((result, index) => (
                  <li key={index}>{stepItemToText(result)}</li>
                ))
              ) : (
                <li>{details.expected_result || 'No expected results defined'}</li>
              )}
            </ol>
          </div>
        </div>
      </div>
    );
  };

  const renderVersionComparison = () => {
    if (selectedVersions.length !== 2) {
      return <p style={{ textAlign: "center", color: "#666" }}>Please select exactly 2 versions to compare.</p>;
    }

    const comparisonKey = selectedVersions.join('__');
    const comparisonDetails = versionDetails[comparisonKey];
    

    if (comparisonLoading) {
      return (
        <div className="version-comparison">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
            <SkeletonLoader />
            <SkeletonLoader />
          </div>
        </div>
      );
    }

    if (comparisonError) {
      return (
        <div className="version-comparison error-state" style={{ padding: "20px", textAlign: "center" }}>
          <p style={{ color: "#dc3545", marginBottom: "10px" }}>{comparisonError}</p>
          <button 
            onClick={() => fetchComparisonDetails(selectedVersions)}
            style={{
              padding: "8px 16px",
              backgroundColor: "#007bff",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer"
            }}
          >
            Retry
          </button>
        </div>
      );
    }

    if (!comparisonDetails || comparisonDetails.length !== 2) {
      return <p style={{ textAlign: "center", color: "#666" }}>Unable to load comparison data.</p>;
    }

    // Sort by created_at to ensure older on left, newer on right
    const [olderVersion, newerVersion] = comparisonDetails[0].created_at <= comparisonDetails[1].created_at
      ? [comparisonDetails[0], comparisonDetails[1]]
      : [comparisonDetails[1], comparisonDetails[0]];

    return (
      <div className="version-comparison">
        <h3 style={{ marginBottom: "20px" }}>
          Comparing Version {olderVersion.version} to {newerVersion.version}
        </h3>

        <div className="comparison-metadata" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", marginBottom: "20px" }}>
          <div className="comparison-column">
            <h4 style={{ marginTop: "0" }}>Version {olderVersion.version} (Newer)</h4>
            <div className="metadata-item">
              <span className="metadata-label">Name:</span>
              <TruncatedTestCaseName name={olderVersion.name} />
            </div>
            <div className="metadata-item">
              <span className="metadata-label">Created:</span>
              <span className="metadata-value">{formatDateTime(olderVersion.created_at)}</span>
            </div>
            <div className="metadata-item">
              <span className="metadata-label">By:</span>
              <span className="metadata-value">{olderVersion.created_by}</span>
            </div>
          </div>

          <div className="comparison-column">
            <h4 style={{ marginTop: "0" }}>Version {newerVersion.version} (Older)</h4>
            <div className="metadata-item">
              <span className="metadata-label">Name:</span>
              <TruncatedTestCaseName name={newerVersion.name} />
            </div>
            <div className="metadata-item">
              <span className="metadata-label">Created:</span>
              <span className="metadata-value">{formatDateTime(newerVersion.created_at)}</span>
            </div>
            <div className="metadata-item">
              <span className="metadata-label">By:</span>
              <span className="metadata-value">{newerVersion.created_by}</span>
            </div>
          </div>
        </div>

        <div className="comparison-content">
          <h4 style={{ marginBottom: "15px" }}>Steps</h4>
          <div className="comparison-row" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", marginBottom: "30px" }}>
            <div className="comparison-column">
              <h5 style={{ backgroundColor: "#f8f9fa", padding: "10px", borderRadius: "4px", marginBottom: "10px" }}>
                Version {olderVersion.version}
              </h5>
              <ol style={{ marginLeft: "20px", lineHeight: "1.8" }}>
                {olderVersion.step_description && Array.isArray(olderVersion.step_description) ? (
                  olderVersion.step_description.map((step, index) => (
                    <li key={index}>{stepItemToText(step)}</li>
                  ))
                ) : (
                  <li>{olderVersion.step_description || 'No steps defined'}</li>
                )}
              </ol>
            </div>

            <div className="comparison-column">
              <h5 style={{ backgroundColor: "#f8f9fa", padding: "10px", borderRadius: "4px", marginBottom: "10px" }}>
                Version {newerVersion.version}
              </h5>
              <ol style={{ marginLeft: "20px", lineHeight: "1.8" }}>
                {newerVersion.step_description && Array.isArray(newerVersion.step_description) ? (
                  newerVersion.step_description.map((step, index) => (
                    <li key={index}>{stepItemToText(step)}</li>
                  ))
                ) : (
                  <li>{newerVersion.step_description || 'No steps defined'}</li>
                )}
              </ol>
            </div>
          </div>

          <h4 style={{ marginBottom: "15px" }}>Expected Results</h4>
          <div className="comparison-row" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
            <div className="comparison-column">
              <h5 style={{ backgroundColor: "#f8f9fa", padding: "10px", borderRadius: "4px", marginBottom: "10px" }}>
                Version {olderVersion.version}
              </h5>
              <ol style={{ marginLeft: "20px", lineHeight: "1.8" }}>
                {olderVersion.expected_result && Array.isArray(olderVersion.expected_result) ? (
                  olderVersion.expected_result.map((result, index) => (
                    <li key={index}>{stepItemToText(result)}</li>
                  ))
                ) : (
                  <li>{olderVersion.expected_result || 'No expected results defined'}</li>
                )}
              </ol>
            </div>

            <div className="comparison-column">
              <h5 style={{ backgroundColor: "#f8f9fa", padding: "10px", borderRadius: "4px", marginBottom: "10px" }}>
                Version {newerVersion.version}
              </h5>
              <ol style={{ marginLeft: "20px", lineHeight: "1.8" }}>
                {newerVersion.expected_result && Array.isArray(newerVersion.expected_result) ? (
                  newerVersion.expected_result.map((result, index) => (
                    <li key={index}>{stepItemToText(result)}</li>
                  ))
                ) : (
                  <li>{newerVersion.expected_result || 'No expected results defined'}</li>
                )}
              </ol>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <>
      <div data-rt-modal-overlay className="modal-overlay" onClick={onClose} style={{zIndex:14000}}></div>
      <div className="modal version-history-modal" style={{ maxWidth: "1000px", width: "90%", height: "90vh", maxHeight: "90vh", minHeight: "90vh", borderRadius: "8px", boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)", zIndex: 14500, display: "flex", flexDirection: "column" }}>
        <div className="modal-header" style={{ borderBottom: "1px solid #e9ecef", padding: "16px 24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h2 style={{ margin: "0", fontSize: "1.4rem", fontWeight: "600", color: "#212529" }}>
            Test Case Version History
            {testCase?.name && (
              <>
                {': '}
                <TruncatedTestCaseName
                  name={testCase.name}
                  className=""
                  title={testCase.name}
                />
              </>
            )}
          </h2>
          <button className="modal-close" onClick={onClose}>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        
        <div className="modal-body" style={{ padding: "24px", flex: 1, overflowY: "auto", minHeight: 0 }}>
          {loading ? (
            <div className="loading-container" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "40px 0" }}>
              <div className="loading-spinner" style={{ width: "40px", height: "40px", border: "4px solid #f3f3f3", borderTop: "4px solid #3498db", borderRadius: "50%", animation: "spin 1s linear infinite" }}></div>
              <p style={{ marginTop: "16px", color: "#666" }}>Loading version history...</p>
            </div>
          ) : (
            <div className="version-history-container">
              <div className="version-controls" style={{ marginBottom: "20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <h3 style={{ margin: "0" }}>Versions</h3>
                <div className="version-actions">
                  {versions.length > 1 && (
                    <button 
                      className={`btn ${compareMode ? 'btn-primary' : 'btn-outline'}`}
                      onClick={() => {
                        setCompareMode(!compareMode);
                        setSelectedVersions([]);
                        setDetailsError(null);
                        setComparisonError(null);
                      }}
                      style={{
                        padding: "8px 16px",
                        borderRadius: "4px",
                        fontSize: "0.9rem",
                        cursor: "pointer",
                        backgroundColor: compareMode ? "#007bff" : "white",
                        color: compareMode ? "white" : "#333",
                        border: "1px solid #ccc"
                      }}
                    >
                      {compareMode ? "Exit Compare Mode" : "Compare Versions"}
                    </button>
                  )}
                </div>
              </div>
              
              <div className="version-history-content" style={{ display: "flex", gap: "20px" }}>
                <div className="version-list" style={{ flex: "0 0 250px", borderRight: "1px solid #eee", paddingRight: "20px" }}>
                  {versions.map(version => {
                    const versionKey = `${version.test_case_id}_${version.version}`;
                    return (
                      <div 
                        key={versionKey}
                        className={`version-item ${selectedVersions.includes(versionKey) ? 'selected' : ''}`}
                        onClick={() => handleVersionSelect(version)}
                        style={{
                          padding: "12px",
                          borderRadius: "6px",
                          marginBottom: "10px",
                          cursor: "pointer",
                          backgroundColor: selectedVersions.includes(versionKey) ? "#e6f7ff" : "#f8f9fa",
                          border: selectedVersions.includes(versionKey) ? "1px solid #91d5ff" : "1px solid #eee",
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
                          <span style={{ fontWeight: "600", fontSize: "1rem" }}>Version {version.version}</span>
                          {version.is_current && (
                            <span style={{
                              padding: "2px 8px",
                              borderRadius: "10px",
                              fontSize: "0.7rem",
                              backgroundColor: "#d4edda",
                              color: "#155724"
                            }}>
                              Current
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: "0.8rem", color: "#666" }}>
                          {formatDate(version.created_at)}
                        </div>
                        <div style={{ fontSize: "0.8rem", color: "#666", marginTop: "4px" }}>
                          By: {version.created_by}
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                <div className="version-detail-panel" style={{ flex: "1" }}>
                  {compareMode ? (
                    renderVersionComparison()
                  ) : (
                    selectedVersions.length === 1 ? (
                      renderVersionDetails(selectedVersions[0])
                    ) : (
                      <div className="empty-selection" style={{ textAlign: "center", color: "#666", padding: "40px 20px" }}>
                        <p>Select a version to view details</p>
                      </div>
                    )
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        
        <div className="modal-footer" style={{
          display: "flex",
          justifyContent: "flex-end",
          padding: "16px 24px",
          borderTop: "1px solid #e9ecef",
          backgroundColor: "#f8f9fa"
        }}>
          <button 
            className="btn btn-outline" 
            style={{ 
              padding: "8px 16px", 
              backgroundColor: "white", 
              border: "1px solid #ccc", 
              borderRadius: "4px", 
              cursor: "pointer" 
            }} 
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </>
  );
};

export default TestCaseVersionHistory;